import { useCallback, useEffect, useState } from "react";
import { Loader, type LoaderOptions } from "@googlemaps/js-api-loader";

import useGoogleMapsApiKey from "./useGoogleMapsApiKey";

type LoaderAttributes = Record<string, string | boolean>;

const DEFAULT_LOADER_OPTIONS: Partial<LoaderOptions> & { attributes?: LoaderAttributes } = {
  id: "google-maps-script",
  version: "weekly",
  attributes: { async: true, defer: true },
};

type LoadedLibraries = {
  mapsLibrary: google.maps.MapsLibrary;
  markerLibrary: google.maps.MarkerLibrary;
};

let cachedLoader: Loader | null = null;
let cachedApiKey: string | null = null;
let cachedPromise: Promise<LoadedLibraries> | null = null;

type LoaderStatus = "idle" | "loading" | "success" | "error";

const getLoaderPromise = (apiKey: string) => {
  if (!cachedLoader || cachedApiKey !== apiKey) {
    cachedApiKey = apiKey;
    const loaderOptions: LoaderOptions & { attributes?: LoaderAttributes } = {
      ...DEFAULT_LOADER_OPTIONS,
      apiKey,
    };

    cachedLoader = new Loader(loaderOptions);
    cachedPromise = null;
  }

  if (!cachedPromise && cachedLoader) {
    cachedPromise = cachedLoader
      .load()
      .then(() => {
        if (typeof google === "undefined" || !google.maps?.importLibrary) {
          throw new Error("Google Maps API not loaded correctly");
        }
        return Promise.all([
          google.maps.importLibrary("maps"),
          google.maps.importLibrary("marker"),
        ]).then(([mapsLibrary, markerLibrary]) => ({
          mapsLibrary: mapsLibrary as google.maps.MapsLibrary,
          markerLibrary: markerLibrary as google.maps.MarkerLibrary,
        }));
      });
  }

  return cachedPromise;
};

const useGoogleMapsLoader = () => {
  const {
    data: googleMapsRuntimeConfig,
    isLoading: isGoogleMapsApiKeyLoading,
    isError: isGoogleMapsApiKeyError,
  } = useGoogleMapsApiKey();
  const googleMapsApiKey = googleMapsRuntimeConfig?.googleMapsApiKey || null;
  const [hasStarted, setHasStarted] = useState(false);
  const [mapsLibrary, setMapsLibrary] = useState<
    google.maps.MapsLibrary | null
  >(null);
  const [markerLibrary, setMarkerLibrary] = useState<
    google.maps.MarkerLibrary | null
  >(null);
  const [isMapsApiLoaded, setIsMapsApiLoaded] = useState(false);
  const [mapsApiError, setMapsApiError] = useState<Error | null>(null);
  const [status, setStatus] = useState<LoaderStatus>("idle");

  const startLoading = useCallback(() => {
    setHasStarted(true);
  }, []);

  useEffect(() => {
    if (!hasStarted || isMapsApiLoaded || isGoogleMapsApiKeyError) {
      return undefined;
    }

    if (!googleMapsApiKey) {
      if (isGoogleMapsApiKeyLoading) {
        setStatus("loading");
      }

      return undefined;
    }

    let isMounted = true;
    const loaderPromise = getLoaderPromise(googleMapsApiKey);

    setStatus("loading");

    loaderPromise
      ?.then(({ mapsLibrary: maps, markerLibrary: marker }) => {
        if (isMounted) {
          setMapsLibrary(maps);
          setMarkerLibrary(marker);
          setIsMapsApiLoaded(true);
          setMapsApiError(null);
          setStatus("success");
        }
      })
      .catch((error: Error) => {
        if (isMounted) {
          setMapsApiError(error);
          setStatus("error");
        }
      });

    return () => {
      isMounted = false;
    };
  }, [
    googleMapsApiKey,
    hasStarted,
    isGoogleMapsApiKeyError,
    isGoogleMapsApiKeyLoading,
    isMapsApiLoaded,
  ]);

  return {
    startLoading,
    status,
    googleMapsApiKey,
    isGoogleMapsApiKeyLoading,
    isGoogleMapsApiKeyError,
    isMapsApiLoaded,
    mapsLibrary,
    markerLibrary,
    mapsApiError,
    googleMapsMapId: googleMapsRuntimeConfig?.googleMapsMapId || null,
    googleMapsLightMapId: googleMapsRuntimeConfig?.googleMapsLightMapId || null,
    googleMapsDarkMapId: googleMapsRuntimeConfig?.googleMapsDarkMapId || null,
  } as const;
};

export default useGoogleMapsLoader;
